// src/GuestSearch.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import countryCodes from "./utils/countryCodes";
import "./guest-search.css";

const API = (() => {
  const configured = (import.meta.env?.VITE_API_BASE || "").replace(/\/+$/, "");
  if (configured) return configured;
  if (location.hostname.endsWith(".pages.dev") || location.hostname.endsWith("bamboome.me")) {
    return "https://api.bamboome.me";
  }
  return "http://127.0.0.1:8000";
})();

function Field({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-2 text-sm sm:text-base">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-100 truncate max-w-[60%] sm:max-w-[70%]">
        {value || "-"}
      </span>
    </div>
  );
}

function Pill({ children, tone = "default" }) {
  const base =
    "inline-flex items-center gap-2 px-2.5 py-1 rounded-full border text-xs sm:text-sm select-none";
  const tones = {
    default: "bg-slate-800/60 border-slate-700 text-slate-200",
    good: "bg-emerald-900/40 border-emerald-700 text-emerald-200",
    warn: "bg-amber-900/40 border-amber-700 text-amber-200",
    bad: "bg-rose-900/40 border-rose-700 text-rose-200",
  };
  return <span className={`${base} ${tones[tone]}`}>{children}</span>;
}

const isImage = (name = "", mime = "") =>
  /\.(png|jpe?g|webp|gif)$/i.test(name) || /^image\//i.test(mime);
const isPDF = (name = "", mime = "") =>
  /\.pdf$/i.test(name) || /^application\/pdf$/i.test(mime);

/* ---------- vCard helpers ---------- */
const escapeVCF = (s = "") =>
  String(s)
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");

const buildVCard = (c = {}) => {
  const lines = [];
  const FN = escapeVCF(c.name || "");
  const [first = "", ...rest] = (c.name || "").trim().split(/\s+/);
  const last = rest.join(" ");
  lines.push("BEGIN:VCARD");
  lines.push("VERSION:3.0");
  lines.push(`N:${escapeVCF(last)};${escapeVCF(first)};;;`);
  lines.push(`FN:${FN}`);
  if (c.title) lines.push(`TITLE:${escapeVCF(c.title)}`);
  if (c.company) lines.push(`ORG:${escapeVCF(c.company)}`);
  if (c.email) lines.push(`EMAIL;TYPE=INTERNET:${escapeVCF(c.email)}`);
  if (c.phones) {
    const phones = String(c.phones)
      .split(/[,/|;]+/)
      .map((p) => p.trim())
      .filter(Boolean);
    phones.forEach((p) => lines.push(`TEL;TYPE=CELL:${escapeVCF(p)}`));
  }
  if (c.address) lines.push(`ADR;TYPE=HOME:;;${escapeVCF(c.address)};;;;`);
  if (c.birthday) lines.push(`BDAY:${escapeVCF(c.birthday)}`);
  lines.push("END:VCARD");
  return lines.join("\r\n");
};

const downloadVCard = (c = {}) => {
  const blob = new Blob([buildVCard(c)], { type: "text/vcard;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const safeName = (c.name || "contact").replace(/[^\w\s.-]+/g, "").trim() || "contact";
  a.href = url;
  a.download = `${safeName}.vcf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/* ---------- URL helpers ---------- */
const joinURL = (base, path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const b = (base || "").replace(/\/+$/, "");
  const p = String(path).startsWith("/") ? path : "/" + path;
  return b + p;
};
const urlForView = (f) => {
  if (f?.view_url) return joinURL(API, f.view_url);
  if (f?.download_url) return joinURL(API, f.download_url);
  return "";
};
const urlForDownload = (f) => {
  if (f?.download_url) return joinURL(API, f.download_url);
  if (f?.view_url) {
    const sep = f.view_url.includes("?") ? "&" : "?";
    return joinURL(API, `${f.view_url}${sep}download=1`);
  }
  return "";
};

const getAccessToken = () => {
  try {
    const directKeys = ["access_token", "auth_token", "token", "authToken", "bamboo_token"];
    for (const key of directKeys) {
      const value = (localStorage.getItem(key) || sessionStorage.getItem(key) || "").trim();
      if (value) return value.replace(/^Bearer\s+/i, "");
    }
    for (const key of ["auth", "session", "user"]) {
      const raw = localStorage.getItem(key) || sessionStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const value = parsed?.access_token || parsed?.token || parsed?.authToken;
      if (value) return String(value).replace(/^Bearer\s+/i, "");
    }
  } catch (error) {
    console.warn("Could not read the saved login token", error);
  }
  return "";
};

const apiHeaders = (jsonBody = false) => {
  const headers = {};
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (jsonBody) headers["Content-Type"] = "application/json";
  return headers;
};

const responseError = async (resp) => {
  const text = await resp.text().catch(() => "");
  if (!text) return `${resp.status} ${resp.statusText}`;
  try {
    const parsed = JSON.parse(text);
    const detail = parsed?.detail ?? parsed?.message ?? parsed?.error;
    if (typeof detail === "string") return detail;
    if (detail) return detail.message || JSON.stringify(detail);
  } catch {
    // The response is plain text.
  }
  return text;
};

const splitPhoneNumbers = (value = "") => {
  const items = String(value)
    .split(/[,;|\n]+/)
    .map((item) => item.trim())
    .filter(Boolean);
  return items.length ? items : [""];
};

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const normalizePhoneCanonical = (value = "") => {
  let digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("00")) digits = digits.slice(2);
  if (digits.startsWith("961")) {
    let local = digits.slice(3).replace(/^0+/, "");
    if (!local) return "";
    if (local.length > 8) local = local.slice(-8).replace(/^0+/, "");
    return local ? `961${local}` : "";
  }
  if (digits.startsWith("0") && (digits.length === 7 || digits.length === 8)) {
    return `961${digits.slice(1)}`;
  }
  if (digits.length === 7 || digits.length === 8) return `961${digits}`;
  return digits;
};

const birthdayStringToDate = (value = "") => {
  const text = String(value || "").trim();
  if (/^--\d{2}-\d{2}$/.test(text)) return new Date(`2000-${text.slice(2)}`);
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return new Date(text);
  if (/^\d{2}-\d{2}$/.test(text)) return new Date(`2000-${text}`);
  return null;
};

const birthdayDateToMMDD = (date) => {
  if (!date) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `--${month}-${day}`;
};

const birthdayDisplayText = (value = "") => {
  const date = birthdayStringToDate(value);
  if (!date) return "";
  return date.toLocaleDateString(undefined, { month: "long", day: "numeric" });
};

const renderContactMonthHeader = ({
  date,
  changeMonth,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}) => (
  <div className="gs-birthday-month-head">
    <button type="button" onClick={decreaseMonth} disabled={prevMonthButtonDisabled} title="Previous month">
      ‹
    </button>
    <select value={date.getMonth()} onChange={(event) => changeMonth(Number(event.target.value))} title="Choose month">
      {MONTHS.map((month, index) => (
        <option key={month} value={index}>{month}</option>
      ))}
    </select>
    <button type="button" onClick={increaseMonth} disabled={nextMonthButtonDisabled} title="Next month">
      ›
    </button>
  </div>
);

function NewContactBirthdayPicker({ value, onChange }) {
  return (
    <div className="gs-birthday-picker">
      {!value && (
        <div className="gs-birthday-required" role="alert">
          ⚠️ Birthday is mandatory. Select the month and day.
        </div>
      )}
      <DatePicker
        selected={birthdayStringToDate(value)}
        onChange={(date) => onChange(date ? birthdayDateToMMDD(date) : "")}
        dateFormat="MMMM d"
        showPopperArrow={false}
        placeholderText="Select Month and Day"
        popperPlacement="bottom-start"
        withPortal={typeof window !== "undefined" && window.innerWidth < 768}
        portalId="root"
        autoComplete="off"
        shouldCloseOnSelect
        fixedHeight
        peekNextMonth
        calendarStartDay={1}
        onKeyDown={(event) => event.preventDefault()}
        renderCustomHeader={renderContactMonthHeader}
        formatWeekDay={(dayName) => dayName.slice(0, 3)}
      />
      <div className="gs-birthday-actions">
        <button type="button" onClick={() => onChange(birthdayDateToMMDD(new Date()))}>Today</button>
        <button type="button" onClick={() => onChange("")}>Clear</button>
        <small>{value ? `Selected: ${birthdayDisplayText(value)} (${value})` : "Stores as --MM-DD"}</small>
      </div>
    </div>
  );
}

const blankContactForm = () => ({
  name: "",
  phones: [{ code: "961", number: "" }],
  birthday: "",
  email: "",
  company: "",
  title: "",
  address: "",
});

const CONTACT_DB_FIELDS = [
  { key: "name", label: "Full name", fallback: "name" },
  { key: "phones", label: "Phone numbers", fallback: "phones / phone / mobile" },
  { key: "birthday", label: "Birthday", fallback: "birthday / birth_date / dob" },
  { key: "email", label: "Email", fallback: "email" },
  { key: "company", label: "Company", fallback: "company" },
  { key: "title", label: "Title", fallback: "title" },
  { key: "address", label: "Address", fallback: "address" },
];

export default function GuestSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [searchMeta, setSearchMeta] = useState(null);

  const [editingIndex, setEditingIndex] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    phones: [""],
    email: "",
    company: "",
    title: "",
    birthday: "",
    address: "",
  });
  const [renameBusy, setRenameBusy] = useState(false);
  const [renameResult, setRenameResult] = useState(null);
  const [backupBusy, setBackupBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(blankContactForm);
  const [createBusy, setCreateBusy] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [createError, setCreateError] = useState("");
  const [contactsSchema, setContactsSchema] = useState(null);
  const [schemaError, setSchemaError] = useState("");

  const [voucherMap, setVoucherMap] = useState({});
  const [voucherLoading, setVoucherLoading] = useState({});
  const [voucherError, setVoucherError] = useState({});

  const [expanded, setExpanded] = useState({});
  const toggleExpanded = (idx) => setExpanded((m) => ({ ...m, [idx]: !m[idx] }));

  // Fullscreen viewer state
  const [viewer, setViewer] = useState({ open: false, idx: null, at: 0 });

  // Blob URL state for viewer
  const [viewerSrc, setViewerSrc] = useState("");
  const [viewerMime, setViewerMime] = useState("");
  const [viewerBusy, setViewerBusy] = useState(false);
  const [viewerErr, setViewerErr] = useState("");

  // ✅ DELETE additions: per-row delete state
  const [deleteBusy, setDeleteBusy] = useState({}); // { [idx]: true/false }

  const visibleContactColumns = useMemo(
    () =>
      CONTACT_DB_FIELDS.map((field) => {
        const actual = Array.from(
          new Set(
            (contactsSchema?.targets || []).flatMap(
              (target) => target?.field_columns?.[field.key] || []
            )
          )
        );
        return { ...field, columns: actual.length ? actual : [field.fallback] };
      }),
    [contactsSchema]
  );
  const columnsFor = (fieldKey) =>
    visibleContactColumns.find((field) => field.key === fieldKey)?.columns.join(", ") || fieldKey;

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${API}/guests/contacts-schema`, {
      method: "GET",
      mode: "cors",
      credentials: "include",
      headers: apiHeaders(),
      signal: controller.signal,
    })
      .then(async (resp) => {
        if (!resp.ok) throw new Error(await responseError(resp));
        return resp.json();
      })
      .then((data) => {
        setContactsSchema(data);
        setSchemaError("");
      })
      .catch((error) => {
        if (error?.name === "AbortError") return;
        setSchemaError(error?.message || "Could not read contacts.db columns.");
      });
    return () => controller.abort();
  }, []);

  const canSearch = useMemo(() => query.trim().length >= 1, [query]);

  const doSearch = async (searchValue = query) => {
    const requested = String(searchValue || "").trim();
    if (!requested) return;
    setLoading(true);
    setErr("");
    try {
      const resp = await fetch(`${API}/guests/global-search?q=${encodeURIComponent(requested)}&limit=100`, {
        method: "GET",
        mode: "cors",
        credentials: "include",
        headers: apiHeaders(),
      });
      if (!resp.ok) {
        throw new Error(await responseError(resp));
      }
      const data = await resp.json();
      const items = data.contacts || [];
      setResults(items);
      setSearchMeta({
        databases: data.scanned_databases || 0,
        branches: data.scanned_branches || [],
        rawMatches: data.raw_matches || 0,
        contactsMatches: data.contacts_db_matches || 0,
        tableMatches: data.table_db_matches || 0,
        linkedPhones: data.linked_phone_count || 0,
        warnings: data.warnings || [],
      });
      setVoucherMap({});
      setVoucherLoading({});
      setVoucherError({});
      setExpanded({});
      setDeleteBusy({});
      closeViewer(); // reset viewer on new search
    } catch (e) {
      console.error(e);
      setResults([]);
      setSearchMeta(null);
      setErr(`Global search failed: ${e.message || "Please check your login and API connection."}`);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") doSearch();
  };

  const startEdit = (idx) => {
    const row = results[idx];
    setEditForm({
      name: row.name || "",
      phones: splitPhoneNumbers(row.phones),
      email: row.email || "",
      company: row.company || "",
      title: row.title || "",
      birthday: row.birthday || "",
      address: row.address || "",
    });
    setRenameResult(null);
    setEditingIndex(idx);
  };
  const cancelEdit = () => setEditingIndex(null);
  const handleEditChange = (e) => setEditForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const updatePhoneNumber = (phoneIndex, value) => {
    setEditForm((form) => ({
      ...form,
      phones: form.phones.map((phone, index) => (index === phoneIndex ? value : phone)),
    }));
  };
  const addPhoneNumber = () => {
    setEditForm((form) => ({ ...form, phones: [...form.phones, ""] }));
  };
  const removePhoneNumber = (phoneIndex) => {
    setEditForm((form) => {
      const phones = form.phones.filter((_, index) => index !== phoneIndex);
      return { ...form, phones: phones.length ? phones : [""] };
    });
  };

  const openNewContact = () => {
    setCreateResult(null);
    setCreateError("");
    setShowCreate(true);
  };
  const closeNewContact = () => {
    if (createBusy) return;
    setShowCreate(false);
  };
  const cancelNewContact = () => {
    if (createBusy) return;
    setCreateForm(blankContactForm());
    setCreateError("");
    setShowCreate(false);
  };
  const handleCreateChange = (event) => {
    const { name, value } = event.target;
    setCreateForm((form) => ({ ...form, [name]: value }));
  };
  const updateCreatePhone = (phoneIndex, field, value) => {
    setCreateForm((form) => ({
      ...form,
      phones: form.phones.map((phone, index) =>
        index === phoneIndex ? { ...phone, [field]: value } : phone
      ),
    }));
  };
  const addCreatePhone = () => {
    setCreateForm((form) => ({
      ...form,
      phones: [...form.phones, { code: "961", number: "" }],
    }));
  };
  const removeCreatePhone = (phoneIndex) => {
    setCreateForm((form) => {
      const phones = form.phones.filter((_, index) => index !== phoneIndex);
      return { ...form, phones: phones.length ? phones : [""] };
    });
  };

  const saveNewContact = async () => {
    const name = String(createForm.name || "").replace(/\s+/g, " ").trim();
    const phones = Array.from(
      new Set(
        createForm.phones
          .map((phone) => normalizePhoneCanonical(`${phone.code || ""}${phone.number || ""}`))
          .filter(Boolean)
      )
    );
    if (!name) {
      setCreateError("Enter the new contact's full name.");
      return;
    }
    if (!phones.length) {
      setCreateError("Enter at least one phone number for the new contact.");
      return;
    }
    if (!createForm.birthday) {
      setCreateError("Birthday is mandatory. Select the contact's month and day.");
      return;
    }
    const confirmed = window.confirm(
      `Add this new contact to contacts.db?\n\nName: ${name}\nPhone: ${phones.join(", ")}\n` +
        `Birthday: ${createForm.birthday || "Not entered"}\n\n` +
        "A complete database backup will be created before the contact is added."
    );
    if (!confirmed) return;

    setCreateBusy(true);
    setCreateError("");
    try {
      const resp = await fetch(`${API}/guests/contacts`, {
        method: "POST",
        mode: "cors",
        credentials: "include",
        headers: apiHeaders(true),
        body: JSON.stringify({
          ...createForm,
          name,
          phones,
          confirmation: "ADD_CONTACTS_DB",
        }),
      });
      if (!resp.ok) throw new Error(await responseError(resp));
      const data = await resp.json();
      setCreateResult(data);
      setCreateForm(blankContactForm());
      setShowCreate(false);
      setQuery(name);
      await doSearch(name);
    } catch (error) {
      console.error(error);
      setCreateError(`Contact was not added: ${error.message || "Unknown error"}`);
    } finally {
      setCreateBusy(false);
    }
  };

  const saveEdit = async () => {
    const original = results[editingIndex];
    if (!original) return;
    const oldName = String(original.name || "").trim();
    const newName = String(editForm.name || "").replace(/\s+/g, " ").trim();
    const newPhones = editForm.phones.map((phone) => phone.trim()).filter(Boolean);
    if (!newName) {
      setErr("The guest name cannot be empty.");
      return;
    }
    if (original.phones && newPhones.length === 0) {
      setErr("Keep at least one phone number or add a replacement number.");
      return;
    }
    const confirmed = window.confirm(
      `Update this complete guest profile everywhere?\n\n${oldName || "(blank)"}  →  ${newName}\n` +
        `Original identity: ${original.phones || "No phone — exact old name only"}\n` +
        `Saved phone numbers: ${newPhones.join(", ") || "None"}\n` +
        `Birthday: ${editForm.birthday || "Not entered"}\n\n` +
        "Name, phones, birthday, email, company, title and address will be synchronized. A full backup is created first."
    );
    if (!confirmed) return;

    setRenameBusy(true);
    setErr("");
    try {
      const resp = await fetch(`${API}/guests/global-update`, {
        method: "POST",
        mode: "cors",
        credentials: "include",
        headers: apiHeaders(true),
        body: JSON.stringify({
          old_name: oldName,
          new_name: newName,
          phones: original.phones || "",
          new_phones: newPhones,
          email: editForm.email,
          company: editForm.company,
          title: editForm.title,
          birthday: editForm.birthday,
          address: editForm.address,
          confirmation: "GLOBAL_PROFILE_UPDATE",
        }),
      });
      if (!resp.ok) {
        throw new Error(await responseError(resp));
      }
      const data = await resp.json();
      setRenameResult(data);
      setEditingIndex(null);
      setQuery(newName);
      await doSearch(newName);
    } catch (e) {
      console.error(e);
      setErr(`Guest profile was not changed: ${e.message || "Global update failed."}`);
    } finally {
      setRenameBusy(false);
    }
  };

  const downloadNameBackup = async (backup = renameResult?.backup) => {
    if (!backup?.download_url) return;
    setBackupBusy(true);
    setErr("");
    try {
      const resp = await fetch(joinURL(API, backup.download_url), {
        method: "GET",
        mode: "cors",
        credentials: "include",
        headers: apiHeaders(),
      });
      if (!resp.ok) throw new Error(await responseError(resp));
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${backup.id || "guest_name_backup"}.zip`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setErr(`Backup download failed: ${e.message || "Unknown error"}`);
    } finally {
      setBackupBusy(false);
    }
  };

  // ✅ DELETE additions: delete handler
  const deleteGuest = async (idx) => {
    const c = results[idx];
    if (!c) return;

    const name = c?.name || "";
    const phones = c?.phones || "";

    const ok = window.confirm(
      `Delete this guest?\n\nName: ${name || "-"}\nPhone: ${phones || "-"}\n\nThis cannot be undone.`
    );
    if (!ok) return;

    setDeleteBusy((m) => ({ ...m, [idx]: true }));

    try {
      const resp = await fetch(`${API}/contacts`, {
        method: "DELETE",
        mode: "cors",
        credentials: "include",
        headers: apiHeaders(true),
        body: JSON.stringify({
          identifier: { name, phones },
        }),
      });

      // Many APIs return 204 No Content for deletes
      if (!resp.ok) {
        const t = await resp.text().catch(() => "");
        throw new Error(`${resp.status} ${resp.statusText}${t ? " — " + t : ""}`);
      }

      // If viewer is open and points to this idx, close it
      if (viewer.open && viewer.idx === idx) {
        closeViewer();
      }

      // Remove from UI + clean maps
      setResults((prev) => prev.filter((_, i) => i !== idx));
      setVoucherMap((m) => {
        const next = {};
        Object.keys(m).forEach((k) => {
          const i = Number(k);
          if (i < idx) next[i] = m[i];
          else if (i > idx) next[i - 1] = m[i];
        });
        return next;
      });
      setVoucherLoading((m) => {
        const next = {};
        Object.keys(m).forEach((k) => {
          const i = Number(k);
          if (i < idx) next[i] = m[i];
          else if (i > idx) next[i - 1] = m[i];
        });
        return next;
      });
      setVoucherError((m) => {
        const next = {};
        Object.keys(m).forEach((k) => {
          const i = Number(k);
          if (i < idx) next[i] = m[i];
          else if (i > idx) next[i - 1] = m[i];
        });
        return next;
      });
      setExpanded((m) => {
        const next = {};
        Object.keys(m).forEach((k) => {
          const i = Number(k);
          if (i < idx) next[i] = m[i];
          else if (i > idx) next[i - 1] = m[i];
        });
        return next;
      });
      setDeleteBusy((m) => {
        const next = {};
        Object.keys(m).forEach((k) => {
          const i = Number(k);
          if (i < idx) next[i] = m[i];
          else if (i > idx) next[i - 1] = m[i];
        });
        return next;
      });
    } catch (e) {
      console.error(e);
      alert("Failed to delete guest");
    } finally {
      setDeleteBusy((m) => ({ ...m, [idx]: false }));
    }
  };

  // Fetch vouchers for one card
  const fetchVouchersForIdx = async (idx) => {
    const c = results[idx];
    if (!c) return [];
    const phone = (c?.phones || "").toString();
    const name = c?.name || "";
    setVoucherLoading((m) => ({ ...m, [idx]: true }));
    setVoucherError((m) => ({ ...m, [idx]: "" }));
    try {
      const resp = await fetch(
        `${API}/vouchers/check?name=${encodeURIComponent(name)}&phone=${encodeURIComponent(phone)}`,
        { method: "GET", mode: "cors", credentials: "include", headers: apiHeaders() }
      );
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(`${resp.status} ${resp.statusText} — ${t}`);
      }
      const data = await resp.json();
      const files = data.files || [];
      setVoucherMap((m) => ({ ...m, [idx]: files }));
      return files;
    } catch (e) {
      console.error(e);
      setVoucherError((m) => ({ ...m, [idx]: "Could not load vouchers." }));
      return [];
    } finally {
      setVoucherLoading((m) => ({ ...m, [idx]: false }));
    }
  };

  // Auto-fetch vouchers when results change
  useEffect(() => {
    if (!results || results.length === 0) return;
    results.forEach((_, idx) => {
      fetchVouchersForIdx(idx);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  const filesForIdx = (idx) => voucherMap[idx] || [];
  const vLoading = (idx) => !!voucherLoading[idx];
  const vError = (idx) => voucherError[idx] || "";
  const dLoading = (idx) => !!deleteBusy[idx];

  // Open viewer helpers
  const openViewer = (idx, at = 0) => setViewer({ open: true, idx, at });
  const closeViewer = () => {
    setViewer({ open: false, idx: null, at: 0 });
    if (viewerSrc) {
      URL.revokeObjectURL(viewerSrc);
      setViewerSrc("");
    }
    setViewerErr("");
  };

  // Ensure we have files then open viewer
  const handleOpenVouchers = async (idx, at = 0) => {
    let files = filesForIdx(idx);
    if (!files.length && !vLoading(idx)) {
      files = await fetchVouchersForIdx(idx);
    }
    if (files.length) openViewer(idx, Math.max(0, Math.min(at, files.length - 1)));
    else alert("No vouchers found for this guest.");
  };

  // Load current file as Blob -> objectURL
  useEffect(() => {
    if (!viewer.open || !Number.isInteger(viewer.idx)) return;
    const files = filesForIdx(viewer.idx);
    const f = files[viewer.at];
    if (!f) return;

    const href = urlForView(f) || urlForDownload(f);
    if (!href) return;

    // cleanup old URL
    if (viewerSrc) {
      URL.revokeObjectURL(viewerSrc);
      setViewerSrc("");
    }
    setViewerMime("");
    setViewerErr("");
    setViewerBusy(true);

    const ac = new AbortController();
    fetch(href, { mode: "cors", credentials: "include", headers: apiHeaders(), signal: ac.signal })
      .then(async (res) => {
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setViewerSrc(url);
        setViewerMime(blob.type || "");
      })
      .catch((e) => {
        console.error("Viewer fetch failed:", e);
        setViewerErr("Could not open this voucher.");
      })
      .finally(() => setViewerBusy(false));

    return () => {
      ac.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewer.open, viewer.idx, viewer.at, voucherMap]);

  // Keyboard nav inside viewer
  const handleViewerKey = useCallback(
    (e) => {
      if (!viewer.open) return;
      if (e.key === "Escape") {
        e.preventDefault();
        closeViewer();
        return;
      }
      if (!Number.isInteger(viewer.idx)) return;
      const files = filesForIdx(viewer.idx);
      if (!files.length) return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        setViewer((v) => ({ ...v, at: (v.at + 1) % files.length }));
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setViewer((v) => ({ ...v, at: (v.at - 1 + files.length) % files.length }));
      }
    },
    [viewer.open, viewer.idx, voucherMap]
  );
  useEffect(() => {
    document.addEventListener("keydown", handleViewerKey);
    return () => document.removeEventListener("keydown", handleViewerKey);
  }, [handleViewerKey]);

  // Touch swipe for mobile
  const swipeRef = useRef({ x: 0, y: 0, dx: 0, dy: 0, moved: false });
  const onTouchStart = (e) => {
    const t = e.touches && e.touches[0];
    if (!t) return;
    swipeRef.current = { x: t.clientX, y: t.clientY, dx: 0, dy: 0, moved: false };
  };
  const onTouchMove = (e) => {
    const t = e.touches && e.touches[0];
    if (!t) return;
    const dx = t.clientX - swipeRef.current.x;
    const dy = t.clientY - swipeRef.current.y;
    swipeRef.current.dx = dx;
    swipeRef.current.dy = dy;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 16) {
      e.preventDefault();
      swipeRef.current.moved = true;
    }
  };
  const onTouchEnd = () => {
    const { dx, dy, moved } = swipeRef.current;
    if (!viewer.open || !Number.isInteger(viewer.idx) || !moved) return;
    const files = filesForIdx(viewer.idx);
    if (!files.length) return;
    const THRESH = 40;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > THRESH) {
      if (dx < 0) {
        setViewer((v) => ({ ...v, at: (v.at + 1) % files.length }));
      } else {
        setViewer((v) => ({ ...v, at: (v.at - 1 + files.length) % files.length }));
      }
    }
    swipeRef.current = { x: 0, y: 0, dx: 0, dy: 0, moved: false };
  };

  // Lock page scroll whenever a fullscreen viewer/modal is open.
  useEffect(() => {
    if (viewer.open || showCreate) {
      document.body.classList.add("no-scroll");
      return () => document.body.classList.remove("no-scroll");
    }
  }, [viewer.open, showCreate]);

  useEffect(() => {
    if (!showCreate) return;
    const closeOnEscape = (event) => {
      if (event.key === "Escape" && !createBusy) setShowCreate(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [showCreate, createBusy]);

  // Keyboard open on collapsed row
  const onVoucherRowKey = (e, idx) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleOpenVouchers(idx, 0);
    }
  };

  return (
    <div className="gs-page min-h-screen bg-slate-950 text-slate-100">
      {/* header */}
      <div className="mx-auto max-w-5xl px-4 pt-6 pb-2">
        <div className="gs-page-header flex items-center justify-between gap-3">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">🔍 Search Guest</h1>
          <div className="gs-header-actions">
            <button
              type="button"
              className="gs-add-contact-trigger"
              onClick={openNewContact}
              aria-expanded={showCreate}
            >
              <span aria-hidden="true">＋</span> Add New Contact
            </button>
            <Link to="/" className="shrink-0">
              <button
                type="button"
                className="btn btn-primary bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-4 py-2 text-sm shadow"
              >
                ← Home
              </button>
            </Link>
          </div>
        </div>
      </div>

      {showCreate ? (
        <div
          className="gs-new-contact-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-contact-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeNewContact();
          }}
        >
          <section className="gs-new-contact-modal" onMouseDown={(event) => event.stopPropagation()}>
            <header className="gs-new-contact-header">
              <div>
                <h2 id="add-contact-title">Add New Contact</h2>
                <p>Enter the contact information below.</p>
              </div>
              <button
                type="button"
                className="gs-create-close"
                onClick={closeNewContact}
                disabled={createBusy}
                aria-label="Close new contact popup"
              >
                ✕
              </button>
            </header>

            <div className="gs-new-contact-body">
              <div className="gs-save-target">
                Saving to: <strong>contacts.db</strong>
                {contactsSchema?.database_count > 0 && (
                  <span>{contactsSchema.database_count} compatible database{contactsSchema.database_count === 1 ? "" : "s"}</span>
                )}
              </div>

              <div className="gs-columns-guide" aria-label="Visible contacts database columns">
                <div className="gs-columns-title">contacts.db columns</div>
                <div className="gs-column-chips">
                  {visibleContactColumns.map((field) => (
                    <div className="gs-column-chip" key={field.key}>
                      <strong>{field.label}</strong>
                      <code>{field.columns.join(" / ")}</code>
                    </div>
                  ))}
                </div>
                {schemaError && <div className="gs-schema-note">Using standard columns: {schemaError}</div>}
                {contactsSchema?.warnings?.length > 0 && (
                  <div className="gs-schema-note">{contactsSchema.warnings.join(" · ")}</div>
                )}
              </div>

              <div className="gs-modal-contact-form">
                <label>
                  <span>Full name <b>*</b></span>
                  <small>contacts.db: {columnsFor("name")}</small>
                  <input
                    type="text"
                    name="name"
                    value={createForm.name}
                    onChange={handleCreateChange}
                    placeholder="Name *"
                    autoFocus
                  />
                </label>

                <div className="gs-create-phone-block">
                  <div className="gs-create-phone-head">
                    <label>
                      <span>Phone numbers <b>*</b></span>
                      <small>contacts.db: {columnsFor("phones")}</small>
                    </label>
                    <button type="button" className="gs-add-phone" onClick={addCreatePhone}>
                      + Add another number
                    </button>
                  </div>
                  {createForm.phones.map((phone, phoneIndex) => (
                    <div className="gs-modal-phone-row" key={`new-contact-phone-${phoneIndex}`}>
                      <Select
                        options={countryCodes.map((country) => ({
                          value: country.code,
                          label: country.label,
                        }))}
                        value={{
                          value: phone.code || "961",
                          label:
                            countryCodes.find((country) => country.code === (phone.code || "961"))?.label ||
                            "Lebanon (+961)",
                        }}
                        onChange={(option) => updateCreatePhone(phoneIndex, "code", option?.value || "961")}
                        placeholder="Search Country"
                        classNamePrefix="react-select"
                        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 13000 }) }}
                      />
                      <input
                        type="tel"
                        inputMode="tel"
                        value={phone.number}
                        onChange={(event) => updateCreatePhone(phoneIndex, "number", event.target.value)}
                        placeholder={phoneIndex === 0 ? "Phone *" : "Additional phone"}
                        aria-label={`New contact phone number ${phoneIndex + 1}`}
                      />
                      <button
                        type="button"
                        className="gs-remove-phone"
                        onClick={() => removeCreatePhone(phoneIndex)}
                        title="Remove this number"
                        aria-label={`Remove new contact phone number ${phoneIndex + 1}`}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>

                {[
                  ["email", "Email", "email", "Email"],
                  ["company", "Company", "text", "Company"],
                  ["title", "Title", "text", "Title"],
                ].map(([name, label, type, placeholder]) => (
                  <label key={name}>
                    <span>{label}</span>
                    <small>contacts.db: {columnsFor(name)}</small>
                    <input
                      type={type}
                      name={name}
                      value={createForm[name]}
                      onChange={handleCreateChange}
                      placeholder={placeholder}
                    />
                  </label>
                ))}

                <label>
                  <span>Birthday <b>*</b></span>
                  <small>contacts.db: {columnsFor("birthday")}</small>
                  <NewContactBirthdayPicker
                    value={createForm.birthday}
                    onChange={(birthday) => setCreateForm((form) => ({ ...form, birthday }))}
                  />
                </label>

                <label>
                  <span>Address</span>
                  <small>contacts.db: {columnsFor("address")}</small>
                  <input
                    name="address"
                    value={createForm.address}
                    onChange={handleCreateChange}
                    placeholder="Address"
                  />
                </label>
              </div>

              {createError && <div className="gs-create-error" role="alert">{createError}</div>}
            </div>

            <footer className="gs-new-contact-footer">
              <button type="button" className="gs-cancel-contact-btn" onClick={cancelNewContact} disabled={createBusy}>
                Cancel
              </button>
              <button
                type="button"
                className="gs-save-contact-btn"
                onClick={saveNewContact}
                disabled={createBusy}
              >
                {createBusy ? "Saving…" : "💾 Save Contact"}
              </button>
            </footer>
          </section>
        </div>
      ) : null}

      {/* search bar */}
      <div className="mx-auto max-w-5xl px-4 pb-4">
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-3 sm:p-4 shadow-xl">
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Type name or phone…"
              className="flex-1 rounded-xl bg-slate-800/70 border border-slate-700 px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button
              onClick={() => doSearch()}
              disabled={!canSearch || loading}
              className="btn btn-primary rounded-xl px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white shadow"
            >
              {loading ? "Searching…" : "Search"}
            </button>
          </div>
          {err && <div className="mt-2 text-rose-300 text-sm">{err}</div>}
          {searchMeta && !loading && (
            <div className="gs-search-meta" role="status">
              <span>
                Checked {searchMeta.databases} database{searchMeta.databases === 1 ? "" : "s"}
                {searchMeta.branches.length > 0 && ` across ${searchMeta.branches.join(", ")}`}
              </span>
              <span className="gs-search-breakdown">
                contacts.db: {searchMeta.contactsMatches} · table.db/tables.db: {searchMeta.tableMatches}
                {searchMeta.linkedPhones > 0 && ` · ${searchMeta.linkedPhones} phone link${searchMeta.linkedPhones === 1 ? "" : "s"}`}
              </span>
            </div>
          )}
          {searchMeta?.warnings?.length > 0 && !loading && (
            <details className="gs-search-warnings">
              <summary>{searchMeta.warnings.length} database warning{searchMeta.warnings.length === 1 ? "" : "s"}</summary>
              {searchMeta.warnings.map((warning, index) => (
                <div key={`${warning}-${index}`}>{warning}</div>
              ))}
            </details>
          )}
        </div>
      </div>

      {createResult?.ok && (
        <div className="mx-auto max-w-5xl px-4 pb-4">
          <div className="gs-rename-success" role="status">
            <div>
              <div className="gs-rename-success-title">✓ New contact added to contacts.db</div>
              <div className="gs-rename-success-copy">
                {createResult.contact?.name} was saved in {createResult.created_databases} database
                {createResult.created_databases === 1 ? "" : "s"}.
              </div>
            </div>
            <button
              type="button"
              className="gs-backup-btn"
              onClick={() => downloadNameBackup(createResult.backup)}
              disabled={backupBusy}
            >
              {backupBusy ? "Preparing…" : "⬇ Download pre-add backup"}
            </button>
          </div>
        </div>
      )}

      {renameResult?.ok && (
        <div className="mx-auto max-w-5xl px-4 pb-4">
          <div className="gs-rename-success" role="status">
            <div>
              <div className="gs-rename-success-title">✓ Complete guest profile updated everywhere</div>
              <div className="gs-rename-success-copy">
                {renameResult.new_name} · {renameResult.changed_cells} changed profile value
                {renameResult.changed_cells === 1 ? "" : "s"} in {renameResult.changed_databases} database
                {renameResult.changed_databases === 1 ? "" : "s"}.
              </div>
            </div>
            <button
              type="button"
              className="gs-backup-btn"
              onClick={() => downloadNameBackup()}
              disabled={backupBusy}
            >
              {backupBusy ? "Preparing…" : "⬇ Download pre-change backup"}
            </button>
          </div>
        </div>
      )}

      {/* results */}
      <div className="mx-auto max-w-5xl px-4 pb-10">
        {!loading && results.length === 0 && query && (
          <div className="text-slate-400 text-center py-10">No results found.</div>
        )}

        {loading && (
          <div className="grid gap-3 sm:gap-4 grid-cols-1">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-28 rounded-2xl bg-slate-800/50 animate-pulse" />
            ))}
          </div>
        )}

        {!loading && results.length > 0 && (
          <div className="grid grid-cols-1 gap-3 sm:gap-4">
            {results.map((c, idx) => {
              const files = filesForIdx(idx);
              const hasVouchers = files.length > 0;

              return (
                <div
                  key={idx}
                  className="gs-card rounded-2xl border border-slate-800 bg-slate-900/60 p-4 shadow-xl"
                >
                  {editingIndex === idx ? (
                    <div className="gs-global-edit space-y-2">
                      <div className="gs-global-edit-title">Edit complete guest profile everywhere</div>
                      <div className="gs-global-edit-help">
                        The original phone identifies the correct guest. You can edit all details and add multiple numbers.
                        Saving creates a complete pre-change database backup.
                      </div>
                      <label className="gs-edit-label" htmlFor={`guest-name-${idx}`}>Full name</label>
                      <input
                        id={`guest-name-${idx}`}
                        name="name"
                        value={editForm.name}
                        onChange={handleEditChange}
                        autoFocus
                        className="w-full rounded-lg bg-slate-800/70 border border-slate-700 px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <div className="gs-phone-editor">
                        <div className="gs-phone-editor-head">
                          <label className="gs-edit-label">Phone numbers</label>
                          <button type="button" className="gs-add-phone" onClick={addPhoneNumber}>
                            + Add number
                          </button>
                        </div>
                        {editForm.phones.map((phone, phoneIndex) => (
                          <div className="gs-phone-row" key={`phone-${phoneIndex}`}>
                            <input
                              type="tel"
                              value={phone}
                              onChange={(event) => updatePhoneNumber(phoneIndex, event.target.value)}
                              placeholder={phoneIndex === 0 ? "+961 71 123 456" : "Additional phone number"}
                              aria-label={`Phone number ${phoneIndex + 1}`}
                              className="w-full rounded-lg bg-slate-800/70 border border-slate-700 px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                            <button
                              type="button"
                              className="gs-remove-phone"
                              onClick={() => removePhoneNumber(phoneIndex)}
                              title="Remove this number"
                              aria-label={`Remove phone number ${phoneIndex + 1}`}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="gs-profile-grid">
                        <label>
                          <span>Birthday</span>
                          <input
                            type="date"
                            name="birthday"
                            value={editForm.birthday}
                            onChange={handleEditChange}
                          />
                        </label>
                        <label>
                          <span>Email</span>
                          <input
                            type="email"
                            name="email"
                            value={editForm.email}
                            onChange={handleEditChange}
                            placeholder="guest@example.com"
                          />
                        </label>
                        <label>
                          <span>Company</span>
                          <input name="company" value={editForm.company} onChange={handleEditChange} />
                        </label>
                        <label>
                          <span>Title</span>
                          <input name="title" value={editForm.title} onChange={handleEditChange} />
                        </label>
                        <label className="gs-profile-wide">
                          <span>Address</span>
                          <textarea
                            name="address"
                            value={editForm.address}
                            onChange={handleEditChange}
                            rows="2"
                          />
                        </label>
                      </div>
                      <div className="gs-identity-row">
                        <span>Original identity (used only to find existing records)</span>
                        <strong>{c.phones || "No phone — exact-name matching only"}</strong>
                      </div>
                      <div className="flex gap-2 pt-1">
                        <button
                          className="btn bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg"
                          onClick={saveEdit}
                          disabled={renameBusy}
                        >
                          {renameBusy ? "Updating all databases…" : "Save complete profile everywhere + backup"}
                        </button>
                        <button
                          className="btn btn-ghost bg-slate-600 hover:bg-slate-700 text-white px-4 py-2 rounded-lg"
                          onClick={cancelEdit}
                          disabled={renameBusy}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* vCard tile */}
                      <div className="vcf-card">
                        <div
                          className="vcf-head"
                          onClick={() => toggleExpanded(idx)}
                          role="button"
                          aria-expanded={expanded[idx] ? "true" : "false"}
                          aria-controls={`vcf-body-${idx}`}
                          title="Click to show more"
                        >
                          <div className="vcf-title">
                            <div className="vcf-name">{c.name || "-"}</div>
                            <div className="vcf-sub">📞 {c.phones || "—"}</div>
                            {Array.isArray(c.matched_branches) && c.matched_branches.length > 0 && (
                              <div className="gs-source-line">
                                {c.matched_branches.map((branch) => (
                                  <span className="gs-source-chip" key={branch}>{branch}</span>
                                ))}
                                <span className="gs-source-count">
                                  {c.source_count || 0} source{c.source_count === 1 ? "" : "s"}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className={`vcf-chev ${expanded[idx] ? "rotate" : ""}`}>▶</div>
                        </div>

                        <div
                          id={`vcf-body-${idx}`}
                          className={`vcf-body ${expanded[idx] ? "open" : ""}`}
                        >
                          <div>
                            <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-6 p-3">
                              <div className="flex-1 space-y-1.5">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                  <Field label="📞 Phone" value={c.phones} />
                                  <Field label="📧 Email" value={c.email} />
                                  <Field label="🏢 Company" value={c.company} />
                                  <Field label="🎩 Title" value={c.title} />
                                  <Field label="🎂 Birthday" value={c.birthday} />
                                  <Field label="📍 Address" value={c.address} />
                                </div>
                              </div>

                              <div className="shrink-0 vcf-actions">
                                <button
                                  className="vcf-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    downloadVCard(c);
                                  }}
                                  title="Download .vcf"
                                >
                                  ⬇️ Download vCard
                                </button>
                                <a
                                  className="vcf-btn"
                                  href={`data:text/vcard;charset=utf-8,${encodeURIComponent(buildVCard(c))}`}
                                  download={`${
                                    (c.name || "contact").replace(/[^\w\s.-]+/g, "").trim() || "contact"
                                  }.vcf`}
                                  onClick={(e) => e.stopPropagation()}
                                  title="Save as .vcf"
                                >
                                  💾 Save vCard
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* voucher summary + actions */}
                      <div className="mt-3 flex items-start gap-2 sm:gap-4">
                        <Pill
                          tone={
                            hasVouchers
                              ? "good"
                              : vError(idx)
                              ? "bad"
                              : vLoading(idx)
                              ? "warn"
                              : "default"
                          }
                        >
                          🎟{" "}
                          {vLoading(idx)
                            ? "Checking…"
                            : hasVouchers
                            ? `${files.length} voucher${files.length > 1 ? "s" : ""}`
                            : vError(idx)
                            ? "Error"
                            : "No voucher"}
                        </Pill>

                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => fetchVouchersForIdx(idx)}
                            className="btn btn-ghost rounded-lg px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white disabled:opacity-60"
                            disabled={vLoading(idx)}
                            title="Refresh vouchers"
                          >
                            Refresh
                          </button>

                          <button
                            onClick={() => startEdit(idx)}
                            className="btn btn-warning rounded-lg px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white"
                          >
                            Edit
                          </button>

                          {/* ✅ DELETE button */}
                          <button
                            onClick={() => deleteGuest(idx)}
                            disabled={dLoading(idx)}
                            className="btn rounded-lg px-3 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white"
                            title="Delete guest"
                          >
                            {dLoading(idx) ? "Deleting…" : "Delete"}
                          </button>
                        </div>
                      </div>

                      {/* Collapsed Voucher Row (opens fullscreen viewer) */}
                      <div className="mt-3 border-t border-slate-800 pt-2">
                        <div
                          role="button"
                          tabIndex={0}
                          aria-label="Open vouchers"
                          onKeyDown={(e) => onVoucherRowKey(e, idx)}
                          onClick={() => handleOpenVouchers(idx, 0)}
                          className={`voucher-collapsed ${hasVouchers ? "" : "disabled"}`}
                          title={hasVouchers ? "Open vouchers" : "No vouchers found"}
                        >
                          <div className="vc-left">
                            <span className="vc-icon">🎟</span>
                            <span className="vc-title">Vouchers</span>
                            <span className="vc-sub">
                              {vLoading(idx)
                                ? "Checking…"
                                : hasVouchers
                                ? `${files.length} file${files.length > 1 ? "s" : ""}`
                                : vError(idx)
                                ? "Error"
                                : "No vouchers"}
                            </span>
                          </div>
                          <div className="vc-right">
                            <span className="vc-hint">{hasVouchers ? "Open" : ""}</span>
                            <span className="vc-chev">▶</span>
                          </div>
                        </div>
                        {vError(idx) && <div className="text-rose-300 text-sm mt-2">{vError(idx)}</div>}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ====== VOUCHER VIEWER MODAL ====== */}
      {viewer.open && Number.isInteger(viewer.idx) && (
        <div className="voucher-dialog" role="dialog" aria-modal="true" onClick={closeViewer}>
          <div className="viewer-card" onClick={(e) => e.stopPropagation()}>
            <div className="viewer-head">
              <div className="viewer-title">🎟 Vouchers</div>
              <button className="viewer-x" onClick={closeViewer}>
                ✕
              </button>
            </div>

            {(() => {
              const files = filesForIdx(viewer.idx);
              const f = files[viewer.at] || {};
              const dlHref = urlForDownload(f);
              const name = f.name || "";

              return (
                <>
                  <div
                    className="viewer-body"
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                  >
                    {viewerBusy ? (
                      <div className="viewer-blank">Loading…</div>
                    ) : viewerErr ? (
                      <div className="viewer-blank">{viewerErr}</div>
                    ) : viewerSrc && isImage(name, viewerMime) ? (
                      <img className="viewer-media" src={viewerSrc} alt={name} />
                    ) : viewerSrc && isPDF(name, viewerMime) ? (
                      <iframe className="viewer-media" src={viewerSrc} title={name} />
                    ) : (
                      <div className="viewer-blank">No preview</div>
                    )}
                  </div>

                  <div className="viewer-foot">
                    <div className="viewer-meta">
                      <div className="viewer-name" title={name}>
                        {name}
                      </div>
                      <div className="viewer-count">
                        {viewer.at + 1} / {files.length}
                      </div>
                    </div>
                    <div className="viewer-actions">
                      <button
                        className="viewer-btn"
                        onClick={() =>
                          setViewer((v) => {
                            const len = files.length || 1;
                            return { ...v, at: (v.at - 1 + len) % len };
                          })
                        }
                        title="Previous (←)"
                      >
                        ← Prev
                      </button>
                      <button
                        className="viewer-btn"
                        onClick={() =>
                          setViewer((v) => {
                            const len = files.length || 1;
                            return { ...v, at: (v.at + 1) % len };
                          })
                        }
                        title="Next (→)"
                      >
                        Next →
                      </button>
                      {dlHref && (
                        <a
                          className="viewer-btn primary"
                          href={dlHref}
                          target="_blank"
                          rel="noreferrer"
                          download
                        >
                          Download
                        </a>
                      )}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
